/* oxlint-disable max-lines */

import * as React from 'react';
import { CountrysideTrapBannerContainer } from 'shared/containers/CountrysideTrapBannerContainer';

import { trackFavoriteAdded, trackFavoriteRemoved, trackPageJumped, trackPhoneOpened } from './tracking';
import { trackBlockedEarlyAccessPhoneClick } from './tracking/trackBlockedEarlyAccessPhoneClick';
import { trackShowEarlyAccessPhoneClick } from './tracking/trackShowEarlyAccessPhoneClick';
import { type TFullOfferProps, type IIntegrateAdOptions } from './types';
import { getCarouselBanner } from './utils/getCarouselBanner';
import { getServicesBanner } from './utils/getServicesBanner';
import { prepareOfferCardComponentsProps } from './utils/prepareOfferCardComponentsProps';
import { willCarouselBannerBeShown } from './utils/willCarouselBannerBeShown';
import { OfferContainer } from '../../../fragments/OfferCardWidget/shared/containers/OfferContainer';
import { isNewbuildingBookedFromDeveloper } from '../../../fragments/OfferCardWidget/shared/utils/newbuildingBooking';
import { getDateRangeValue, getRangeValue, type TJsonQuery } from '../../../packages/JsonQuery';
import { type IJsonQuery } from '../../json_query';
import { type IOffer, type TOfferCategory } from '../../offer/types';
import { EHideOfferState } from '../../serp/state/hide_offer';
import { trackHiddenObjects } from '../../tracking/hide_offer';
import { isDealRentDisablePhonesExperimentActive } from '../../utils/experiments/dealRentDisablePhones';
import { getIsNewbuildingFromJsonQuery } from '../../utils/getIsNewbuildingFromJsonQuery';
import { isRosreestrCheckedLabelAvailable } from '../../utils/isRosreestrCheckedLabelAvailable';
import { isSaleFromSuburbanBuilder } from '../../utils/isSaleFromSuburbanBuilder';
import { getOfferListingType, type TOfferListingType } from '../../utils/offer/tracking';
import { getOfferKpAdvantages } from '../../utils/trackings';
import { ParallaxBanner } from '../ParallaxBanner';
import { SuburbanBuildersProjectsContainer } from '../SuburbanBuildersProjectsContainer';
import { SuburbanListingBanner } from '../SuburbanListingBanner';
import { TGBBanner } from '../TGBBanner';
/**
 * Требования к позициям банsнеров
 * @see https://conf.cian.tech/pages/viewpage.action?pageId=1319907301
 */
const PARALLAX_BANNER_1_POSITION_OFFSET = 10;
const PARALLAX_BANNER_2_POSITION_OFFSET = 20;

const TGP_BANNER_1_POSITION_OFFSET = 5;
const TGP_BANNER_2_POSITION_OFFSET = 15;
const TGP_BANNER_3_POSITION_OFFSET = 20;
const TGP_BANNER_4_POSITION_OFFSET = 25;

const SUBURBAN_LISTING_BANNER_POSITION_OFFSET = 7;
const SUBURBAN_BUILDERS_PROJECTS_POSITION_OFFSET = 12;
/**
 * position !== positionOffset
 */
const COUNTRYSIDE_TRAP_BANNER_POSITION = 1;

export const isDailyrent = (offerCategory: TOfferCategory | null) => {
  return offerCategory && ['dailyHouseRent', 'dailyBedRent', 'dailyFlatRent', 'dailyRoomRent'].includes(offerCategory);
};

interface IUpdateDailyrentUrlParameters {
  currentUrl: string;
  dates?: { checkin: string; checkout: string };
  guestsCount?: number;
}

export const updateUrlWithDailyrentParameters = ({
  currentUrl,
  dates,
  guestsCount,
}: IUpdateDailyrentUrlParameters): string => {
  const url = new URL(currentUrl);

  if (dates) {
    url.searchParams.set('checkin', dates.checkin);
    url.searchParams.set('checkout', dates.checkout);
  } else {
    url.searchParams.delete('checkin');
    url.searchParams.delete('checkout');
  }

  if (guestsCount && guestsCount > 1) {
    url.searchParams.set('min_beds', String(guestsCount));
  } else {
    url.searchParams.delete('min_beds');
  }

  return url.toString();
};

const getUpdatedUrlWithDates = (jsonQuery: TJsonQuery, offer: IOffer) => {
  const isDailyrentOffer = isDailyrent(offer.category);

  const jsonQueryGuests = getRangeValue('beds_count')(jsonQuery);
  const jsonQueryDates = getDateRangeValue('dates')(jsonQuery);

  const guestsCount = jsonQueryGuests?.gte;
  const checkin = jsonQueryDates?.gte;
  const checkout = jsonQueryDates?.lt;

  const hasDates = checkin && checkout;
  const hasGuests = guestsCount && guestsCount > 1;

  const shouldUpdateUrl = (hasDates || hasGuests) && isDailyrentOffer;

  if (!shouldUpdateUrl) {
    return undefined;
  }

  const dates = hasDates ? { checkin, checkout } : undefined;

  return updateUrlWithDailyrentParameters({ currentUrl: offer.fullUrl, dates, guestsCount });
};

export const integrateAd = (options: IIntegrateAdOptions) => {
  const {
    currentPageNumber,
    perPage,
    offers,
    suggestions,
    offerCardCreator,
    isPrintEnabled,
    locationId,
    suggestionFragment,
    jsonQuery,
    hiddenOffers = [],
    hiddenSuggestions = [],
    isAgent,
    showCountrysideTrap,
    extendedOffersOffset,
    keyPrefix,
    isCountrysideBuildersBannerAvailable,
    servicesBannerPositions,
    showSuburbanListingBanner,
    suburbanBuildersProjects,
    isNewbuildingOffersWithBannerCarouselVisible,
  } = options;

  let positionOffset = options.positionOffset || 0;

  const isNewbuilding = getIsNewbuildingFromJsonQuery(jsonQuery);

  const willCarouselBannerBeShownValue = willCarouselBannerBeShown(
    isNewbuildingOffersWithBannerCarouselVisible,
    offers.length,
    options.suggestionFragment,
  );

  return offers.reduce((acc: React.ReactNode[], offer: IOffer, index) => {
    const getBannerKey = (bannerName: string) => `${keyPrefix}_${bannerName}_${offer.id}`;

    const urlWithDates = getUpdatedUrlWithDates(jsonQuery, offer);
    if (urlWithDates) {
      offer.fullUrl = urlWithDates;
    }

    const suggestionIds = (suggestions || []).map(item => item.id);

    if (!hiddenOffers.includes(offer.id) && !hiddenSuggestions.includes(offer.id)) {
      const isOfferExtended = suggestionIds.includes(offer.id);
      const position = (currentPageNumber - 1) * perPage + index;
      // порядок для события считаем начиная с единицы
      let trackingPosition = position + 1;

      // у добивок собственное начало отсчета
      trackingPosition = isOfferExtended ? trackingPosition - (extendedOffersOffset || 0) : trackingPosition;

      const nativeTgbPosition = trackingPosition + 1;

      positionOffset += 1;

      acc.push(
        offerCardCreator(
          offer,
          trackingPosition,
          suggestionFragment.isSimilar ? index + suggestionFragment.startIndex : index,
          currentPageNumber,
          keyPrefix,
        ),
      );

      if (!isPrintEnabled) {
        // TGB BANNER
        if (
          [TGP_BANNER_1_POSITION_OFFSET, TGP_BANNER_2_POSITION_OFFSET, TGP_BANNER_4_POSITION_OFFSET].includes(
            positionOffset,
          ) ||
          (TGP_BANNER_3_POSITION_OFFSET === positionOffset && isNewbuilding)
        ) {
          acc.push(
            <TGBBanner key={getBannerKey('TgpBanner')} position={nativeTgbPosition} isNewbuilding={isNewbuilding} />,
          );
        }

        if (positionOffset === SUBURBAN_LISTING_BANNER_POSITION_OFFSET && showSuburbanListingBanner) {
          acc.push(<SuburbanListingBanner key={getBannerKey('SuburbanListingBanner')} position={nativeTgbPosition} />);
        }

        // COUNTRYSIDE TRAP BANNER
        if (showCountrysideTrap && position === COUNTRYSIDE_TRAP_BANNER_POSITION) {
          acc.push(<CountrysideTrapBannerContainer key={`countryside_banner_${position}`} />);
        }

        // Карусель новостроек
        const carouselBanner = getCarouselBanner({
          positionOffset,
          position,
          index,
          offersLength: offers.length,
          isNewbuildingOffersWithBannerCarouselVisible,
          willCarouselBannerBeShown: willCarouselBannerBeShownValue,
          suggestionFragment: options.suggestionFragment,
          isAgent,
          options,
          locationId,
          isCountrysideBuildersBannerAvailable,
        });

        if (carouselBanner) {
          acc.push(carouselBanner);
        }

        // SERVICES BANNER
        const servicesBanner = getServicesBanner({
          offersLength: offers.length,
          positionOffset,
          position,
          servicesBannerPositions,
          suggestionFragment: options.suggestionFragment,
          willCarouselBannerBeShown: willCarouselBannerBeShownValue,
          isAgent,
          options,
          locationId,
          isCountrysideBuildersBannerAvailable,
        });

        if (servicesBanner) {
          acc.push(servicesBanner);
        }

        // PARALLAX BANNER
        if (
          positionOffset === PARALLAX_BANNER_1_POSITION_OFFSET ||
          (positionOffset === PARALLAX_BANNER_2_POSITION_OFFSET && !isNewbuilding)
        ) {
          acc.push(
            <ParallaxBanner
              key={getBannerKey('ParallaxBanner')}
              isNewbuilding={isNewbuilding}
              position={nativeTgbPosition}
            />,
          );
        }

        if (positionOffset === SUBURBAN_BUILDERS_PROJECTS_POSITION_OFFSET && suburbanBuildersProjects?.length) {
          acc.push(<SuburbanBuildersProjectsContainer />);
        }
      }
    }

    return acc;
  }, []);
};

export const getOnFavoriteChange =
  (
    offer: IOffer,
    jsonQuery: IJsonQuery,
    position: number,
    onFavoriteChangedHandler: (offer: IOffer, isFavorite: boolean) => void,
    source?: string,
  ) =>
  (isFavorite: boolean) => {
    const trackingLabel = offer.gaLabel;

    if (isFavorite) {
      const kpAdvantages = isSaleFromSuburbanBuilder(offer) ? getOfferKpAdvantages(offer) : undefined;

      trackFavoriteAdded(jsonQuery, { offerId: offer.id, position, kpAdvantages }, trackingLabel || undefined, source);
    } else {
      trackFavoriteRemoved(jsonQuery, { offerId: offer.id, position }, trackingLabel || undefined);
    }

    onFavoriteChangedHandler(offer, isFavorite);
  };

interface IGetOfferCardCreator {
  props: TFullOfferProps;
  isSimilar?: boolean;
  isSimilarFromML?: boolean;
  pageNum?: number;
  isListingExtended?: boolean;
  source?: string;
}

// oxlint-disable-next-line display-name
export const getOfferCardCreator =
  ({ props, isSimilar, isSimilarFromML, pageNum, isListingExtended, source }: IGetOfferCardCreator) =>
  (offer: IOffer, position: number, index: number, currentPageNumber: number, keyPrefix: string): React.ReactNode => {
    const {
      breadcrumbs,
      currentPath,
      onFavoriteChanged,
      jsonQuery,
      onUserInfoPopupClose,
      onUserInfoPopupOpen,
      hideOfferAction,
      hideOfferAfterAuthAction,
      qsToUris,
      suggestionDistancesSeoText,
      user,
      offersQty,
      mlRankingGuid,
      mlRankingModelVersion,
      queryString,
      hideOffer: { hideOfferState, hidingOffer },
      maxAuctionBet,
      maxAuctionService,
      trackingData,
      auctionBanks,
      villagePromotionLabel,
      autoScrollSettings,
      addToComparison,
      deleteFromComparison,
      openChat,
      openNotAvailablePopup,
      addToComparisonAfterAuth,
      openAuthenticationPopup,
      openNewbuildingBrokerCallbackPopup,
    } = props;

    const { id } = offer;
    const analyticsListingType: TOfferListingType = isListingExtended ? 'extended' : getOfferListingType(offer);
    const withAuction = Boolean(
      !!auctionBanks && !!offer.user && !!offer.user.cianUserId && auctionBanks.includes(+offer.user.cianUserId),
    );

    const onPopupClose = () => onUserInfoPopupClose(offer.id);
    const onPopupOpen = () => onUserInfoPopupOpen(offer.id);
    const onHideOffer =
      hideOfferState === EHideOfferState.FETCHING || hideOfferState === EHideOfferState.ANIMATING
        ? () => {}
        : () => {
            trackHiddenObjects({ offer, jsonQuery });
            hideOfferAction({ offer, isSimilar: !!isSimilar });
          };
    const onHideOfferAfterAuth = () => {
      hideOfferAfterAuthAction({ offer, isSimilar: !!isSimilar });
    };

    const onPageJumped = (label: string) =>
      trackPageJumped(offer, {
        user,
        jsonQuery,
        position,
        isSimilarFromML,
        label,
        pageNum,
        breadcrumbs,
        queryString,
        offersQty,
        mlRankingGuid,
        mlRankingModelVersion,
        listingType: analyticsListingType,
        source,
      });

    const offerCardProps = prepareOfferCardComponentsProps(props, {
      onPageJumped,
      onPopupClose,
      onPopupOpen,
      onHideOffer,
      onHideOfferAfterAuth,
      onFavoriteChange: getOnFavoriteChange(offer, jsonQuery, position, onFavoriteChanged, source),
      trackPhoneOpened: (isFromBrandBlock?: boolean) =>
        trackPhoneOpened(
          offer,
          position,
          jsonQuery,
          user,
          offersQty,
          queryString,
          analyticsListingType,
          isSimilar,
          isFromBrandBlock,
          source,
        ),
      trackShowEarlyAccessPhoneClick: () =>
        trackShowEarlyAccessPhoneClick(
          offer,
          position,
          jsonQuery,
          user,
          offersQty,
          queryString,
          analyticsListingType,
          source,
        ),
      trackBlockedEarlyAccessPhoneClick: (blockType: string) =>
        trackBlockedEarlyAccessPhoneClick(
          offer,
          position,
          jsonQuery,
          user,
          offersQty,
          queryString,
          blockType,
          analyticsListingType,
          source,
        ),
      offer,
      jsonQuery,
      position,
      addToComparison,
      addToComparisonAfterAuth,
      deleteFromComparison,
    });

    return (
      <OfferContainer
        key={`${keyPrefix}-${id}`}
        index={index}
        currentPath={currentPath}
        suggestionDistancesSeoText={suggestionDistancesSeoText}
        position={position}
        trackingData={trackingData}
        analyticsListingType={isListingExtended ? analyticsListingType : undefined}
        queryString={queryString}
        offer={offer}
        jsonQuery={jsonQuery}
        qsToUris={qsToUris}
        user={user}
        excludedUtilitiesTermsRegions={offerCardProps.excludedUtilitiesTermsRegions}
        maxAuctionBet={maxAuctionBet}
        maxAuctionService={maxAuctionService}
        isHideOfferInProgress={
          (Boolean(hidingOffer && hidingOffer.offer.id === offer.id) && hideOfferState === EHideOfferState.FETCHING) ||
          (Boolean(hidingOffer && hidingOffer.offer.id === offer.id) && hideOfferState === EHideOfferState.ANIMATING)
        }
        api={{
          onAgentRatingChanged: offerCardProps.userInfo.onAgentRatingChanged,
          onPageJumped,
          onHideOffer: offerCardProps.buttons.onHideOffer,
          onHideOfferAfterAuth: offerCardProps.buttons.onHideOfferAfterAuth,
          isPopupsOpened: props.isPopupsOpened,
          onFavoritesChange: offerCardProps.offerCard.onFavoriteChange,
          sendComplaintFeedback: offerCardProps.complaintsHelpers.sendComplaintFeedback,
          onComplaintSent: offerCardProps.reportPopup.onComplaintSent,
          addToComparison: offerCardProps.offerComparison.addToComparison,
          addToComparisonAfterAuth: offerCardProps.offerComparison.addToComparisonAfterAuth,
          deleteFromComparison: offerCardProps.offerComparison.deleteFromComparison,
          openChat,
          openNotAvailablePopup,
          openAuthenticationPopup,
          openNewbuildingBrokerCallbackPopup,
        }}
        isBot={offerCardProps.pessimisation.isBot}
        tracking={offerCardProps.tracking}
        featureToggle={offerCardProps.featureToggle}
        withAuction={withAuction}
        isDealRentDisablePhonesExperimentActive={Boolean(
          offer.offerInDeal && isDealRentDisablePhonesExperimentActive(props.abUseExperiments),
        )}
        isRosreestrCheckedLabelAvailable={isRosreestrCheckedLabelAvailable(offer)}
        isNewbuildingBookedFromDeveloper={isNewbuildingBookedFromDeveloper(offer)}
        villagePromotionLabel={villagePromotionLabel}
        isSoldFurnishedLabelAvailable={offer.isSoldFurnished}
        comparison={props.comparison}
        allOfferChatOnboardings={props.allOfferChatOnboardings}
        autoScrollSettings={autoScrollSettings}
      />
    );
  };
